import { usePathname } from 'next/navigation';
import type { MouseEvent } from 'react';
import { useCallback } from 'react';

import { EventType } from '@/analytics/types';
import usePathnameWithoutLocale from '@/app/hooks/usePathnameWithoutLocale';

interface UsePageAnalytics {
  onFooterLinkClick: (e: MouseEvent<HTMLAnchorElement>) => Promise<void>;
  onFooterSocialLinkClick: (e: { name: string; url: string }) => Promise<void>;
  onHamburgerClick: ({ open }: { open: boolean }) => Promise<void>;
  onHeaderLinkClick: (e: MouseEvent<HTMLAnchorElement>) => Promise<void>;
  onMenuLinkClick: (e: MouseEvent<HTMLAnchorElement>) => Promise<void>;
  onMenuShopButtonClick: (e: MouseEvent<HTMLAnchorElement>) => Promise<void>;
  onShopButtonClick: (e: MouseEvent<HTMLButtonElement>) => Promise<void>;
  sendEmailSubmitAnalytics: (email: string) => Promise<void>;
}

const usePageAnalytics = (): UsePageAnalytics => {
  const path = usePathname();
  const pathnameWithoutLocale = usePathnameWithoutLocale();

  // click links in footer
  const handleFooterLinkClick = useCallback(
    async (event: MouseEvent<HTMLAnchorElement>): Promise<void> => {
      const { href } = event.currentTarget;
      const url = new URL(href);
      await window.ouraAnalytics.track(EventType.CTAClicked, {
        location: 'footer',
        path: url.pathname,
      });
    },
    [],
  );

  // click social links in footer
  const handleFooterSocialLinkClick = useCallback(
    async ({ name, url }: { name: string; url: string }): Promise<void> => {
      await window.ouraAnalytics.track(EventType.LinkClicked, {
        cta: name,
        location: 'footer',
        path: url,
      });
    },
    [],
  );

  // open and close the menu
  const handleHamburgerClick = useCallback(
    async ({ open }: { open: boolean }): Promise<void> => {
      await window.ouraAnalytics.track(EventType.HamburgerClicked, {
        location: 'header',
        action: open ? 'open' : 'close',
      });
    },
    [],
  );

  // click links in header
  const handleHeaderLinkClick = useCallback(
    async (event: MouseEvent<HTMLAnchorElement>): Promise<void> => {
      const { href } = event.currentTarget;
      const targetPath = new URL(href).pathname;

      // Prevent firing if already on the target page
      if (pathnameWithoutLocale === targetPath) {
        return;
      }

      await window.ouraAnalytics.track(EventType.LinkClicked, {
        cta: href,
        location: 'header',
      });
    },
    [pathnameWithoutLocale],
  );

  // click links in menu
  const handleMenuLinkClick = useCallback(
    async (event: MouseEvent<HTMLAnchorElement>): Promise<void> => {
      const { href, innerText } = event.currentTarget;
      const targetPath = new URL(href).pathname;

      // Prevent firing if already on the target page
      if (pathnameWithoutLocale === targetPath) {
        return;
      }

      await window.ouraAnalytics.track(EventType.LinkClicked, {
        cta: innerText,
        location: 'menu',
        path: targetPath,
      });
    },
    [pathnameWithoutLocale],
  );

  // click shop now button in menu
  const handleMenuShopButtonClick = useCallback(async (): Promise<void> => {
    await window.ouraAnalytics.track(EventType.CTAClicked, {
      cta: 'shop_now',
      action: 'go_to_pdp',
      location: 'menu',
      path,
    });
  }, [path]);

  // click shop now button in header
  const handleShopButtonAnalytics = async (): Promise<void> => {
    await window.ouraAnalytics.track(EventType.CTAClicked, {
      cta: 'shop_now',
      action: 'go_to_pdp',
      location: 'header',
      path,
    });
  };

  // submit email form in footer
  const sendEmailSubmitAnalytics = async (_email: string): Promise<void> => {
    const email = _email.toLowerCase();
    await window.ouraAnalytics.identify({ email });

    await Promise.all(
      [
        {
          name: EventType.EmailSignupCompleted,
          properties: { path, email },
        },
        {
          name: EventType.UserEmailCaptured,
          properties: { email },
        },
        { name: EventType.MarketingOptInFooter, properties: {} },
      ].map(({ name, properties }) =>
        window.ouraAnalytics.track(name, properties),
      ),
    );
  };

  return {
    onFooterLinkClick: handleFooterLinkClick,
    onFooterSocialLinkClick: handleFooterSocialLinkClick,
    onHamburgerClick: handleHamburgerClick,
    onHeaderLinkClick: handleHeaderLinkClick,
    onMenuLinkClick: handleMenuLinkClick,
    onMenuShopButtonClick: handleMenuShopButtonClick,
    onShopButtonClick: handleShopButtonAnalytics,
    sendEmailSubmitAnalytics,
  };
};

export default usePageAnalytics;
